import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Col, Row} from "reactstrap";
import {InputForm} from "../../base/input/input";
import Button from "../../base/button/button";
import Form from "../../base/form";
import {DatePickerForm} from "../../base/date-picker";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import classnames from "classnames";
import DateUtils from "../../../../core/services/date-utils";
import {MuiRFF, Yup} from '../../form';
import MultiCustomerSearchSectionAutocomplete from "../../text-fields/autocomplete/customer-autocomplete/multi-search-section";

const schema = Yup.object().shape({
    keyword: Yup.string().nullable(),
    partNo: Yup.string().nullable(),
    startDate: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .maxDate(Yup.ref('endDate'), ValidateMessages.lessThan('End Date'))
        .nullable(),
    endDate: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .minDate(Yup.ref('startDate'), ValidateMessages.greaterThan('Start Date'))
        .nullable(),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const OrderSearchSection = ({
                                hideCustomersMultiSelect = false,
                                searchData,
                                showPartNumberField,
                            }) => {
    const {setCustomers: searchDataSetCustomers} = searchData;
    const [customers, _setCustomers] = useState(searchData.customers)

    /**
     * Sets the initial values of the customers in the state.
     */
    useEffect(() => {
        _setCustomers(searchData.customers);
    }, [searchData.customers]);

    /**
     * Sets the customers in the state and the search data.
     * @type {function(*=): void}
     */
    const setCustomers = useCallback((stateOrCallback) => {
        _setCustomers(prevState => {
            let newState;
            if (typeof stateOrCallback === 'function') {
                newState = stateOrCallback(prevState);
            } else {
                newState = stateOrCallback;
            }
            searchDataSetCustomers(newState);
            return newState;
        })
    }, [searchDataSetCustomers]);

    const initialValues = useMemo(() => ({
        ...searchData.filters,
        startDate: searchData.filters?.startDate ? DateUtils.serverToUTC(searchData.filters?.startDate) : undefined,
        endDate: searchData.filters?.endDate ? DateUtils.serverToUTC(searchData.filters?.endDate) : undefined,
    }), [searchData.filters])


    /**
     * Changes the query of the url to the query containing the searched values so that the list pages of orders can
     * use them and search related orders.
     * @param values {any} form values
     */
    const onSubmit = useCallback((values) => {
        searchData.setFilters({
            ...values,
            startDate: values.startDate ? DateUtils.UTCToServer(values.startDate).format(DateUtils.withoutOffsetFormat) : undefined,
            endDate: values.endDate ? DateUtils.UTCToServer(values.endDate).format(DateUtils.withoutOffsetFormat) : undefined,
        });
        searchDataSetCustomers(customers);
    }, [searchData, customers, searchDataSetCustomers])

    return (
        <>
            <Form
                onSubmit={onSubmit}
                validate={validate}
                initialValues={initialValues}
                className={'d-flex flex-row flex-wrap'}
                render={({submitting, values}) => {
                    const colClassName = 'px-2 mb-4';
                    return (
                        <>
                            {
                                !hideCustomersMultiSelect &&
                                <Col xs={12} className={colClassName}>
                                    <MultiCustomerSearchSectionAutocomplete
                                        customers={customers}
                                        setCustomers={setCustomers}
                                    />
                                </Col>
                            }
                            <Col xs={12} className={classnames('mb-4', 'p-0')}>
                                <Row className={'w-100 no-gutters'}>
                                    <Col className={classnames('flex-grow-1 px-2')}>
                                        <InputForm
                                            label={'Keywords'}
                                            name={'keyword'}
                                            type={'text'}
                                            required={required.keyword}
                                        />
                                    </Col>
                                    <Col
                                        className={`${showPartNumberField ? 'flex-grow-1 px-2' : 'flex-grow-0'} transition`}>
                                        {
                                            showPartNumberField && (
                                                <InputForm
                                                    paperClassName={'transition'}
                                                    label={'Part Number'}
                                                    name={'partNo'}
                                                    type={'text'}
                                                    required={required.partNo}
                                                />
                                            )
                                        }
                                    </Col>
                                </Row>
                            </Col>
                            <Col xs={6} className={colClassName}>
                                <DatePickerForm
                                    name={'startDate'}
                                    label={'Start Date'}
                                    required={required.startDate}
                                    maxDate={values.endDate}
                                />
                            </Col>
                            <Col xs={6} className={colClassName}>
                                <DatePickerForm
                                    name={'endDate'}
                                    label={'End Date'}
                                    required={required.endDate}
                                    minDate={values.startDate}
                                />
                            </Col>
                            <Col xs={12} className='px-3 mt-2 align-items-center row no-gutters justify-content-center align-items-center'>
                                <Button
                                    disabled={!!submitting}
                                    type={'submit'}
                                    variant={'contained'}
                                    color={'primary'}
                                    className='py-3 px-8'
                                >
                                    Search
                                </Button>
                            </Col>
                        </>
                    );
                }}
            />
        </>
    );
}

export default OrderSearchSection;
